import Vue from "vue";
import Vuex from "vuex";
Vue.use(Vuex);
import VueCookies from "vue-cookies";
Vue.use(VueCookies);

import axios from "axios";
import LocalStorageService from "./LocalStorageService";
import webAppConfig from "../commons/webApp.config";
import { commonStore } from "./commonStore";
import { clientStore } from "./clientStore";
import { classificationStore } from "./classificationStore";
import { signupAttribution } from "./attribution";
import { resetDisplayOnSignOut } from "../commons/readability";
import { endPortalSessionsOnSignOut } from "../portals/portalSignOut";
import appLogo from "../../assets/images/logo/inistate-logo.png";

const requestConfig = {
  baseURL: webAppConfig.baseURL,
};

// function getClient() {
//   let client = axios.create({
//     ...requestConfig,
//     headers: {
//       "Content-Type": "application/json",
//       "Authorization": "Bearer " + LocalStorageService.getAccessToken(),
//     },
//   });
//   // Interceptors
//   client.interceptors.response.use((response) => {
//     return response;
//   }, function (error) {
//     const originalRequest = error.config;
//     if (error.response && error.response.status === 401 && !originalRequest._retry) {
//         let localStorage = LocalStorageService;
//         originalRequest._retry = true;
//         const config = {
//           ...requestConfig,
//           headers: {
//             'Content-Type': 'application/x-www-form-urlencoded'
//           }
//         };
//         const data = `grant_type=refresh_token&refresh_token=${localStorage.getRefreshToken()}`;
//         return axios.post('/token', data, config)
//           .then(res => {
//             if (res.status === 200) {
//               localStorage.setToken(res.data);
//               client.defaults.headers['Authorization'] = 'Bearer ' + localStorage.getAccessToken();
//               originalRequest.headers['Authorization'] = 'Bearer ' + localStorage.getAccessToken();
//               return axios(originalRequest);
//             }
//           })
//           .catch(err => {
//             console.error(['Authorization:Interceptor'], err);
//             authorizationStore.dispatch('logout');
//           });
//     } else if (error.response && error.response.status === 401) {
//       authorizationStore.dispatch('logout');
//     } else {
//       console.error(['Authorization:Interceptor'], error);
//     }
//     return Promise.reject(error);
//   });
//   return client;
// }

const setAuth = (authData, state) => {
  state.logoutError = null;
  LocalStorageService.setToken(authData);
  let commonClient = commonStore.state.client;
  commonClient.defaults.headers['Authorization'] = "Bearer " + authData.access_token;
  // state.client = getClient();
  state.client = commonStore.state.client;
}

export const authorizationStore = new Vuex.Store({
  state: {
    // client: getClient(),
    client: commonStore.state.client,
    userName: LocalStorageService.getUserName(),
    roles: LocalStorageService.getRoles(),
    userProfile: {},
    appConfig: null,
    logoutError: null,
    runAs: null,
    // Demo mode: an internal user opts to see the app as a normal user would,
    // hiding every alpha/beta/development feature. Restored per server on load.
    demoMode: Boolean(LocalStorageService.getDemoMode(webAppConfig.baseServer)),
    // API connections (MCP connectors) capability: {enabled, permitted} from
    // GET /api/connections/availability — feature flag + whether the caller's
    // seat tier qualifies under the pricing gate. null until loaded; drives
    // the sidenav Connections entry.
    connectionsAvailability: null,
    // Module Store apps (SS05963) capability: {enabled, permitted} from
    // GET /api/apps/availability — kill switch + workspace-admin gate. null
    // until loaded; drives the sidenav Apps entry (alpha, internal only).
    appsAvailability: null
  },
  mutations: {
    setUserName(state, payload) {
      state.userName = payload;
    },
    setConnectionsAvailability(state, payload) {
      state.connectionsAvailability = payload;
    },
    setAppsAvailability(state, payload) {
      state.appsAvailability = payload;
    },
    setRoles(state, data) {
      state.roles = data;
    },
    setAPIKey(state, data) {
      state.userProfile = {
        ...state.userProfile,
        apiKey: data
      }
    },
    setUserProfile(state, data) {
      state.userProfile = {
        ...data,
        userName: state.userName || LocalStorageService.getUserName(),
      }
    },
    setAppConfig(state, data) {
      state.appConfig = data;
    },
    demoMode(state, data) {
      state.demoMode = Boolean(data);
    }
  },
  actions: {
    /**
     * Toggle (or explicitly set) demo mode. When on, isInternal / isBeta /
     * isDevelopment and workspaceBeta all collapse to false, so demos and
     * screen recordings never leak unreleased functionality. Persisted per server.
     * @param {boolean} [mode] omit to toggle, or pass a boolean to set explicitly.
     */
    setDemoMode({ commit, state }, mode) {
      const value = typeof mode === 'boolean' ? mode : !state.demoMode;
      commit('demoMode', value);
      LocalStorageService.setDemoMode(webAppConfig.baseServer, value);
    },
    async refreshToken({ state, dispatch }, payload) {
      const refreshToken = payload || LocalStorageService.getRefreshToken();

      if (!refreshToken || refreshToken == 'undefined') {
        dispatch('logout', { status: 461 });
        throw new Error('461 No Refresh Token.');
      }

      const maxRetries = 3;
      for (let retryCount = 1; retryCount <= maxRetries; retryCount++) {
        try {
          const res = await axios.post(
            '/token',
            `grant_type=refresh_token&refresh_token=${refreshToken}`,
            {
              ...requestConfig,
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              }
            }
          );

          setAuth(res.data, state);
          break;
        } catch (err) {
          console.error(`Authorization:RefreshToken. Attempt ${retryCount}.`, err);
          const errorResponseStatus = err.response && err.response.status;

          if (retryCount >= maxRetries || errorResponseStatus === 401) {
            console.error("Authorization:RefreshToken. Autologout.", err);
            let status = null;
            if (errorResponseStatus === 400) {
              status = 462;
            } else if (errorResponseStatus == 500) {
              status = 463;
            }
            dispatch('logout', { status: status });
            throw err;
          }

          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    },
    /* SS06445: one refresh for the MCP consent page. Unlike refreshToken it never signs out on a failure:
       logout() replaces the route with /login and would drop the mcp_nonce / mcp_callback the page needs.
       The caller shows the password form instead. */
    async refreshTokenQuietly({ state }) {
      const refreshToken = LocalStorageService.getRefreshToken();
      if (!refreshToken || refreshToken == 'undefined') throw new Error('No refresh token.');
      const res = await axios.post(
        '/token',
        `grant_type=refresh_token&refresh_token=${refreshToken}`,
        { ...requestConfig, headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );
      setAuth(res.data, state);
    },
    async login({ state }, payload) {
      try {
        let url = null;

        if(payload.secret) {
          url = 'grant_type=password&username=' + payload.username + '&password=' + encodeURIComponent(payload.password) + '&secret=' + payload.secret + '&otp=' + payload.otp;
        } else if (payload.otp) {
          url = 'grant_type=password&username=' + payload.username + '&password=' + encodeURIComponent(payload.password) + '&otp=' + payload.otp;
        } else {
          url = 'grant_type=password&username=' + payload.username + '&password=' + encodeURIComponent(payload.password);
        }

        // SS05897: signing in through a join link whose workspace enforces 2FA — relay the policy so the
        // backend prompts authenticator enrollment now, during sign-in, instead of on the next login.
        if (payload.requiresMFA) {
          url += '&requiresMFA=true';
        }

        const res = await axios.post(
            '/token',
            url,
            {
              ...requestConfig,
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              },
            }
          );

        if (res.data) {
          setAuth(res.data, state);
        } else {
          throw 'error';
        }
      } catch (err) {
        console.error("[Authorization]", err);
        throw err;
      }
    },
    async getDemoAuthCode({ }, payload) {
      return await axios.get('/api/workspace/demotoken/' + payload, requestConfig);
    },
    async getDemoSolution({ }, payload) {
      return await axios.post(`https://inistate.com/api/getsolution/${payload}`);
    },
    async joinWorkspaceInformation({ }, payload) {
      return await axios.post('/api/workspace/join', payload, requestConfig);
    },
    async joinWorkspace({ state, dispatch }, payload) {
      try {
        const body = { 'registration': payload.registration, 'code': payload.code };
        // SS05898: authenticator enrollment submitted to complete a join into a 2FA-enforced workspace.
        if (payload.secret) {
          body.secret = payload.secret;
          body.otp = payload.otp;
        }
        const res = await state.client.post(
          '/api/workspace/join',
          body,
          requestConfig
        );
        // MFA-enforced workspace, user not yet enrolled: backend returns a 2fa_register setup payload and
        // does NOT complete the join. Skip the token refresh and hand the challenge back to the caller.
        if (res.data && res.data.error === '2fa_register') {
          return res;
        }
        await dispatch('refreshToken');
        return res;
      } catch (err) {
        throw err;
      }
    },
    async createAccount({ dispatch }, payload) {
      try {
        // Email verification (docs/identity-email-verification-proposal.md, D-7): this client can
        // show the code screen, so it says so; the server answers { verification, clientSecret }
        // when verification is on and nothing is created until the code is entered.
        // Where this sign-up came from, read from the marketing site's cookies and this app's own
        // touch (docs/identity-signup-funnel-proposal.md P1). Absent keys simply mean "not known".
        const res = await axios.post('/api/user/create',
          { ...payload, client: 'web', verification: true, attribution: signupAttribution() },
          requestConfig);
        if (res.data && res.data.verification) {
          return res.data;
        }
        return await dispatch('completeCreateAccount', { data: res.data, requiresMFA: payload && payload.requiresMFA });
      } catch (err) {
        console.error("[Authorization]", err);
        throw err;
      }
    },
    // The tail every sign-up completion shares: an MFA enrollment payload is handed back, an
    // authCode is redeemed for a token.
    async completeCreateAccount({ dispatch }, { data, requiresMFA }) {
      // SS05894: MFA-enforced workspace — backend returns a 2fa_register setup payload instead of an
      // authCode. Hand it back so the caller can drive authenticator enrollment.
      if (data && data.error === '2fa_register') {
        return data;
      }
      if (data && data.authCode) {
        await dispatch('refreshToken', data.authCode);
      } else if (requiresMFA) {
        throw 'mfa setup payload undefined';
      } else {
        throw 'authCode undefined';
      }
      return data;
    },
    async createAccountVerify({ dispatch }, payload) {
      const res = await axios.post('/api/user/create/verify', payload, requestConfig);
      return await dispatch('completeCreateAccount', { data: res.data, requiresMFA: false });
    },
    async createAccountResend({ }, payload) {
      const res = await axios.post('/api/user/create/resend', payload, requestConfig);
      return res.data;
    },
    async createAccountLink({ dispatch }, payload) {
      const res = await axios.post('/api/user/create/link', payload, requestConfig);
      return await dispatch('completeCreateAccount', { data: res.data, requiresMFA: false });
    },
    // Step-up for a signed-in account whose address was never verified (D-6): the code goes to
    // the account's own email; a correct code sets emailConfirmed on the profile.
    async verifyEmailStart({ state }) {
      const res = await state.client.post('/api/user/verifyemail/start', {}, requestConfig);
      return res.data;
    },
    async verifyEmailResend({ state }, payload) {
      const res = await state.client.post('/api/user/verifyemail/resend', payload, requestConfig);
      return res.data;
    },
    async verifyEmailConfirm({ state, commit }, payload) {
      const res = await state.client.post('/api/user/verifyemail/confirm', payload, requestConfig);
      commit('setUserProfile', { ...state.userProfile, emailConfirmed: true });
      return res.data;
    },
    // Legacy link-based verification (the internal-domain login gate): anonymous, by address.
    async sendVerificationEmail({ }, payload) {
      return await axios.post('/api/user/sendverificationemail', payload, requestConfig);
    },
    async verifyEmailLink({ }, payload) {
      return await axios.post('/api/user/verifyemail', payload, requestConfig);
    },
    async setupWorkspace({ state, dispatch }, payload) {
      try {
        // A partner link's ?ref= code is kept in the onboarding_ref cookie by the login page, and
        // WorkspaceRegistration binds it as `reference`. Adding it here covers every caller at once
        // — the wizard, the workspace modal and the connector flows — so WorkSpace.Source finally
        // records the partner that referred the workspace (funnel P0). A caller's own value wins.
        const body = { ...payload };
        if (!body.reference) {
          const reference = Vue.$cookies.get('onboarding_ref');
          if (reference) body.reference = reference;
        }
        if (!body.attribution) body.attribution = signupAttribution();
        const res = await state.client.post('/api/workspace/register', body, requestConfig);
        await dispatch('refreshToken');
        return res;
      } catch (err) {
        console.error("[Authorization]", err);
        throw err;
      }
    },
    /* The vendor company the signed-in account acts for, or null (docs/billing-vendor-lead-onboarding-proposal.md
       in the SVN repo). Only its people see "Set up a workspace for a customer". */
    async getVendor({ state }) {
      const res = await state.client.get('/api/vendor', requestConfig);
      return (res && res.data && res.data.vendor) || null;
    },
    /* The one form (V1): a workspace for a customer, billed by the caller's vendor, with the contact
       invited as a Workspace Admin. As with register, the token carries the new workspace only once refreshed. */
    async setupVendorCustomer({ state, dispatch }, payload) {
      const res = await state.client.post('/api/vendor/customers', payload, requestConfig);
      await dispatch('refreshToken');
      return res;
    },
    async register({ }, payload) {
      return await axios.post('/api/workspace/register',
        { ...payload, attribution: signupAttribution() }, requestConfig);
    },
    async forgotPassword({ }, payload) {
      return await axios.post('/api/user/forgotpassword', payload, requestConfig);
    },
    async resetPassword({ }, payload) {
      payload.newPassword = payload.password;
      return await axios.post('/api/user/forgotpassword', payload, requestConfig);
    },
    async externalSignUp({ state }, payload) {
      try {
        const res = await axios.post('/api/user/externallogin',
          { ...payload, client: 'web', attribution: signupAttribution() }, requestConfig);
        if (res.data && res.data.authCode) {
          const auth = await axios.post(
            '/token',
            `grant_type=refresh_token&refresh_token=${res.data.authCode}`,
            {
              ...requestConfig,
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              },
            }
          );
          if (auth.data) {
            const workspaces = await axios.get('/api/workspace?search=', {
              ...requestConfig,
              headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + auth.data.access_token,
              },
            });
            return {
              workspaces: workspaces.data || [],
              setAuth: () => setAuth(auth.data, state)
            }
          } else {
            throw 'failed to authenticate';
          }
        } else if (res.data.redirect) {
          return { redirect: res.data.redirect };
        } else {
          throw 'failed to login';
        }
      } catch (err) {
        console.error("[Authorization]", err);
        throw err;
      }
    },
    async externalLogin({ state }, payload) {
      // A login, but the auto-register path creates an account through this call, so it carries the
      // source too - the server ignores it when nothing is created.
      let result = await axios.post('/api/user/externallogin',
        { ...payload, client: 'web', attribution: signupAttribution() }, requestConfig);
      if (result && result.data && result.data.authCode) {
        try {
          const res = await axios.post(
            '/token',
            `grant_type=refresh_token&refresh_token=${result.data.authCode}`,
            {
              ...requestConfig,
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              },
            }
          );
          if (res.data) {
            setAuth(res.data, state);
            return true;
          } else {
            throw 'error';
          }
        } catch (err) {
          console.error("[Authorization]", err);
          throw err;
        }
      }

      return result;
    },
    async activate({ state }, payload) {
      let result = await axios.post('/api/workspace/activate', payload, requestConfig);
      if (result && result.data && result.data.authCode) {
        try {
          const res = await axios.post(
            '/token',
            `grant_type=refresh_token&refresh_token=${result.data.authCode}`,
            {
              ...requestConfig,
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
              },
            }
          );
          if (res.data) {
            const authorizationData = res.data
            localStorage.setItem('ls.authorizationData', JSON.stringify({
              access_token: authorizationData.access_token,
              refresh_token: authorizationData.refresh_token,
              /* username does not return */
              // userName: authorizationData.userName,
            }));

            var commonClient = commonStore.state.client;
            commonClient.defaults.headers['Authorization'] = "Bearer " + authorizationData.access_token;
            // state.client = getClient();
            state.client = commonStore.state.client;

            return true;
          } else {
            throw 'error';
          }
        } catch (err) {
          console.error("[Authorization]", err);
          throw err;
        }
      }

      return false;
    },
    async profile({ state, dispatch, commit }) {
      const maxRetries = 3;
      for (let retryCount = 1; retryCount <= maxRetries; retryCount++) {
        try {
          const res = await state.client.get('/api/profile');

          if (!res.data || !res.data.userName) {
            throw new Error("Profile not available or not found.");
          }

          // set userName
          commit("setUserName", res.data.userName);
          // set roles
          commit("setRoles", res.data.roles);
          // set user profile
          let userProfile = res.data;
          delete userProfile.roles;
          commit("setUserProfile", userProfile);
          // set local storage
          const lsAuthData = JSON.parse(localStorage.getItem("ls.authorizationData"));
          localStorage.setItem('ls.authorizationData', JSON.stringify({
            ...lsAuthData,
            userName: res.data.userName,
          }));
          // Non-blocking: resolve whether the Connections feature applies to
          // this user (feature flag + seat-tier pricing gate).
          dispatch('loadConnectionsAvailability');
          dispatch('loadAppsAvailability');
          break;
        } catch (err) {
          console.error(`Authorization:Profile. Attempt ${retryCount}.`, err);
          const errorResponseStatus = err.response && err.response.status;

          if (retryCount >= maxRetries || errorResponseStatus === 401) {
            console.error("Authorization:Profile. Autologout", err);
            dispatch('logout');
            throw err;
          }

          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    },
    setRoles({ commit }, payload) {
      commit('setRoles', payload);
    },
    async loadConnectionsAvailability({ state, commit }) {
      try {
        const res = await state.client.get('/api/connections/availability');
        commit('setConnectionsAvailability', {
          enabled: !!(res.data && res.data.enabled),
          permitted: !!(res.data && res.data.permitted),
        });
      } catch (err) {
        // Older backend (404) or transient failure: treat as unavailable.
        commit('setConnectionsAvailability', { enabled: false, permitted: false });
      }
    },
    async loadAppsAvailability({ state, commit }) {
      try {
        const res = await state.client.get('/api/apps/availability');
        commit('setAppsAvailability', {
          enabled: !!(res.data && res.data.enabled),
          permitted: !!(res.data && res.data.permitted),
        });
      } catch (err) {
        // Older backend (404) or transient failure: treat as unavailable.
        commit('setAppsAvailability', { enabled: false, permitted: false });
      }
    },
    setAPIKey({ commit }, payload) {
      commit('setAPIKey', payload);
    },
    async updateUserProfile({ state, commit }, payload) {
      try {
        await state.client.post('/api/user/update', payload);
        commit('setUserProfile', payload);
      } catch (err) {
        console.error("[Authorization]", err);
        throw err;
      }
    },
    async changePassword({ state }, payload) {
      return await state.client.post('/api/user/changepassword', payload);
    },
    async forgotPassword({ }, payload) {
      /**
       * case a: for sending forget password email
       * case b: for new password
       * e.g.  : payload => email(a,b), code(b), newPassword(b)
       */
      return await axios.post('/api/user/forgotpassword', payload, requestConfig);
    },
    logout({ state }, { status } = {}) {
      // error
      if (status) {
        if (status == 461) { // No Refresh Token
          state.logoutError = "461 No Refresh Token.";
        } else if (status == 462) { // Refresh token Error 400
          state.logoutError = "462 Invalid Refresh Token. This user may have been locked out.";
        } else if (status == 463) { // Refresh token Error 500
          state.logoutError = "463 Refresh token error.";
        } else if (status == 464) { // No security token
          state.logoutError = "464 No credentials.";
        } else if (status == 465) { // Retry 401
          state.logoutError = "465 Invalid authorization.";
        }
      }
      /* PL-17: end this person's portal sessions while the token still works. Fire and forget: never
         awaited, and its failure never stops a sign-out. Not on a forced one (a status): the token
         was refused already. */
      if (!status) endPortalSessionsOnSignOut(LocalStorageService.getAccessToken(), webAppConfig.baseURL);
      /* clear local storage */
      LocalStorageService.clearToken();
      LocalStorageService.clearWorkspace(webAppConfig.baseServer);
      /* reading preference: the login page and the next person start at Default; this
         person's saved choice stays for their next sign-in (FTR00375) */
      resetDisplayOnSignOut();
      /* reset */
      state.userName = null;
      state.roles = null;
      state.userProfile = {};
      // state.client = getClient();
      state.client = commonStore.state.client;
      state.runAs = null;
      const clients = [
        clientStore.state.client,
        classificationStore.state.client
      ];
      clients.forEach(client => {
        client.defaults.headers['wsId'] = null;
      });
      let commonClient = commonStore.state.client;
      commonClient.defaults.headers['Authorization'] = null;
      /* redirect */
      window.router.replace({ name: 'login' });
    },
    async getAPIKey({ state }) {
      return await state.client.get('/api/user/generateapikey');
    },
    setAppConfig({ commit }, payload) {
      commit('setAppConfig', payload);
    },
    setAuth({ state }, payload) {
      setAuth(payload, state);
    },
    setRunAs({ state }, payload = {}) {
      const { workspaceId, target } = payload;
      const wsId = target ? `runas:${workspaceId}:${target}` : workspaceId;
      state.runAs = target ?
        {
          wsId: `runas:${workspaceId}:${target}`,
          target: target
        } : null;
      LocalStorageService.clearWorkspace(webAppConfig.baseServer);
      const clients = [
        state.client,
        clientStore.state.client,
        classificationStore.state.client
      ];
      clients.forEach(client => {
        if (!!wsId)
          client.defaults.headers['wsId'] = wsId;
        else
          delete client.defaults.headers['wsId'];
      });
    }
  },
  getters: {
    userName: state => state.userName,
    userProfile: state => state.userProfile || {},
    userRoles: state => {
      const dict = [
        { value: "Administrator", order: 0 },
        { value: "Consultant", order: 1 },
        { value: "Workspace Admin", order: 2 },
        { value: "User Admin", order: 3 },
      ];

      if (Array.isArray(state.roles) && state.roles.length) {
        let roles = state.roles;
        if (roles.includes("Consultant") && !roles.includes("Workspace Admin")) {
          roles = roles.filter(role => role !== "Consultant");
        }

        if (roles.length) {
          return roles
            .map(x => {
              const role = dict.find(y => y.value === x);
              return role ? role : { value: x, order: 99 }
            })
            .sort((key, keyB) => key.order - keyB.order)
            .map(x => x.value)
            .join(", ");
        }
      }
      return "Member";
    },
    isAdministrator: (state, getters) =>
      getters.userRoles.indexOf("Administrator") > -1,
    isAdminUser: (state, getters) =>
      getters.userRoles.indexOf("Administrator") > -1 ||
      getters.userRoles.indexOf("Workspace Admin") > -1 ||
      getters.userRoles.indexOf("Consultant") > -1,
    isManagerUser: (state, getters) =>
      getters.userRoles.indexOf("User Admin") > -1,
    isBeta: (state, getters) =>
      !state.demoMode && (
        getters.userRoles.indexOf("Consultant") > -1 ||
        getters.userRoles.indexOf("Administrator") > -1),
    /*
     * Raw internal-identity check, unaffected by demo mode. Gate the demo-mode
     * toggle itself on this so the switch stays visible (and reversible) while
     * demo mode is on. Everything else should use isInternal below, which
     * collapses to false in demo mode.
     */
    isInternalIdentity: (state) =>
      state.userName && state.userName.slice(-17) === '@gneysoftware.com' ||
      state.userName && state.userName.slice(-13) === '@inistate.com' ||
      ['admin', 'szewah'].indexOf(state.userName) > -1,
    isInternal: (state, getters) =>
      !state.demoMode && getters.isInternalIdentity,
    /* API connections (MCP connectors): feature on AND this user's seat
     * qualifies under the pricing gate (Standard/Pro, or Administrator). */
    connectionsAvailable: (state) =>
      !!(state.connectionsAvailability
        && state.connectionsAvailability.enabled
        && state.connectionsAvailability.permitted),
    /* Module Store apps (alpha): feature on — the sidenav entry shows for any
     * internal user; the page itself explains the admin-only install gate. */
    appsAvailable: (state) =>
      !!(state.appsAvailability && state.appsAvailability.enabled),
    /* development */
    isDevelopment: (state) =>
      !state.demoMode &&
      ['jiaxiang.tan@gneysoftware.com', 'admin'].indexOf(state.userName) > -1,
    demoMode: (state) => state.demoMode,
    isRunAs: (state) => !!state.runAs,
    runAsWsId: (state) => state.runAs && state.runAs.wsId ? state.runAs.wsId : null,
    appName: state => state.appConfig && state.appConfig.name || 'Inistate',
    appLogo: state => state.appConfig && state.appConfig.logo || appLogo,
    appDisableThirdPartyLogin: state => Boolean(state.appConfig && state.appConfig.disableThirdPartyLogin),
    // Client-first onboarding (docs/frontend-client-home-unification-proposal.md P1, ruled
    // 2026-08-29; docs/frontend-guided-onboarding-proposal.md P0). On: the sign-up wizard is one
    // slide (name the workspace) and every new workspace lands in the Client shell. Off: today's
    // Basic/Expert fork, Basic -> type:'lite' -> Lite shell.
    // These three default ON: the flags now only exist to switch the new flow back off, so an
    // absent key — an empty webApp.config.json, or a config fetch that never landed — still gets
    // the shipped behaviour. Set the key to false in webApp.config.json to opt out.
    appClientFirstOnboarding: state => !(state.appConfig && state.appConfig.clientFirstOnboarding === false),
    // Guided onboarding P1 (docs/frontend-guided-onboarding-proposal.md): the optional questionnaire
    // the wizard asks once the workspace exists. Rides on clientFirstOnboarding, which is what puts
    // the questions after workspace creation rather than before it.
    appGuidedOnboarding: state => !(state.appConfig && state.appConfig.guidedOnboarding === false),
    // Guided onboarding P2: after the questions, a starter workflow installs with sample records.
    // Rides on guidedOnboarding, whose answers are what rank the starters.
    appGuidedFirstWorkflow: state => !(state.appConfig && state.appConfig.guidedFirstWorkflow === false),
    // Workspace settings area (docs/frontend-workspace-settings-proposal.md, W-25). The release
    // STAGE in components/settings/settingsStage.js decides who gets in — alpha means internal
    // staff only. This key is just the kill switch: false fails the area closed for everyone,
    // staff included, with no rebuild.
    appWorkspaceSettings: state => !(state.appConfig && state.appConfig.workspaceSettingsV2 === false),
    // Studio schedules (docs/scheduling-routine-automation-proposal.md §7.1): the module card's
    // Schedule adder and the automation panel's Scheduled tab. Default ON (ruling 2026-09-26, like the
    // server's CanvasRoutinesEnabled): set "studioSchedule": false in webApp.config.json to hide the
    // adder. Existing schedules (for example ones an administrator converted) always open in schedule
    // mode, flag or not.
    appStudioSchedule: state => !(state.appConfig && state.appConfig.studioSchedule === false)
  }
})